import { Link } from 'react-router-dom';
import type { Report } from '@/mocks/reports';

interface ReportCardProps {
  report: Report;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ReportCard({ report, variant = 'default' }: ReportCardProps) {
  if (variant === 'compact') {
    return (
      <article className="report-card flex gap-3 items-start py-3 border-b border-background-200 last:border-b-0 group cursor-pointer">
        <Link to={`/reports/${report.id}`} className="flex gap-3 items-start w-full">
          <img
            src={report.imageUrl}
            alt={report.imageAlt}
            className="w-20 h-16 object-cover object-top rounded flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="tag-badge">{report.categoryLabel}</span>
              {report.isHot && (
                <span className="tag-badge dark">热</span>
              )}
            </div>
            <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {report.title}
            </h3>
            <p className="text-xs text-foreground-500 mt-1">{report.date}</p>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'featured') {
    return (
      <article className="report-card relative overflow-hidden rounded-lg group cursor-pointer">
        <Link to={`/reports/${report.id}`} className="block">
          <div className="relative h-72 md:h-80">
            <img
              src={report.imageUrl}
              alt={report.imageAlt}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="tag-badge dark">{report.categoryLabel}</span>
                {report.isHot && (
                  <span className="inline-flex items-center gap-1 text-xs text-accent-300 font-semibold">
                    <i className="ri-fire-line" /> 热门
                  </span>
                )}
              </div>
              <h2 className="text-base md:text-lg font-heading font-bold text-background-50 group-hover:text-primary-200 transition-colors leading-snug line-clamp-2">
                {report.title}
              </h2>
              <p className="text-xs text-background-300 mt-1.5 line-clamp-2 leading-relaxed hidden sm:block">
                {report.summary}
              </p>
              <p className="text-xs text-background-400 mt-2">{report.date} · 阅读约{report.readTime}分钟</p>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="report-card bg-background-50 border border-background-200 rounded-lg overflow-hidden group cursor-pointer">
      <Link to={`/reports/${report.id}`} className="block">
        <div className="relative h-44 overflow-hidden">
          <img
            src={report.imageUrl}
            alt={report.imageAlt}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4">
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <span className="tag-badge">{report.categoryLabel}</span>
            {report.isHot && <span className="tag-badge dark">热</span>}
          </div>
          <h3 className="font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors text-sm md:text-base leading-snug line-clamp-2">
            {report.title}
          </h3>
          <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
            {report.summary}
          </p>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
            <span className="text-xs text-foreground-500">{report.date}</span>
            <span className="text-xs text-foreground-500">约{report.readTime}分钟</span>
          </div>
        </div>
      </Link>
    </article>
  );
}